import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Snackbar,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Add as AddIcon, Delete as DeleteIcon, Person as PersonIcon, Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import Footer from '../components/Footer';

function NouvelleFacture() {
  const [clients, setClients] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [selectedClient, setSelectedClient] = useState(null);
  const [openClientDialog, setOpenClientDialog] = useState(false);
  const navigate = useNavigate();

  const [factureData, setFactureData] = useState({
    client_id: '',
    date_facture: new Date(),
    date_echeance: null,
    notes: '',
    maison_repos: '',
    lignes: [],
  });

  const [nomsMaisonsApi, setNomsMaisonsApi] = useState(() => new Set());
  const [maisonsRepos, setMaisonsRepos] = useState([{ value: 'Autre', label: 'Autre (nom manuel)', fromApi: false }]);

  const [newLigne, setNewLigne] = useState({
    article_id: '',
    quantite: 1,
    prix_unitaire: 0,
    tva: 21.0,
  });

  const [customMaisonRepos, setCustomMaisonRepos] = useState('');
  const [editingMaisonRepos, setEditingMaisonRepos] = useState(null);
  const [editingValue, setEditingValue] = useState('');

  const [newClientData, setNewClientData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    adresse: '',
    code_postal: '',
    ville: '',
    pays: 'Belgique',
    numero_tva: '',
    maison_repos: '',
  });

  const fetchData = useCallback(async (cancelRef) => {
    const isStale = () => cancelRef.cancelled;
    try {
      // Pas d’AbortController sur axios : avec React 18 Strict Mode, l’abort annule tout le
      // Promise.all et peut empêcher de peupler la liste des maisons. On ignore seulement les réponses obsolètes.
      // Paramètre _ pour limiter les réponses JSON mises en cache par certains navigateurs / proxies.
      const ts = Date.now();
      const [clientsRes, articlesRes, maisonsRes] = await Promise.all([
        axios.get('/api/clients'),
        axios.get('/api/articles'),
        axios.get('/api/maisons-repos', { params: { _: ts } }),
      ]);
      if (isStale()) return;

      setClients(Array.isArray(clientsRes.data) ? clientsRes.data : []);
      setArticles(Array.isArray(articlesRes.data) ? articlesRes.data : []);

      const data = maisonsRes.data;
      const arr = Array.isArray(data) ? data : [];
      const valid = arr.filter((m) => {
        if (!m || typeof m !== 'object') return false;
        const nom = m.nom != null ? String(m.nom).trim() : '';
        if (nom.length === 0) return false;
        // Exclusion volontaire de "Notre foyer" des menus déroulants de sélection
        if (nom.toLowerCase() === 'notre foyer') return false;
        return true;
      });
      setNomsMaisonsApi(new Set(valid.map((m) => String(m.nom).trim())));
      setMaisonsRepos([
        ...valid.map((m) => ({
          value: String(m.nom).trim(),
          label: String(m.nom).trim(),
          fromApi: true,
          id: m.id,
        })),
        { value: 'Autre', label: 'Autre (nom manuel)', fromApi: false },
      ]);
      if (valid.length === 0) {
        setSnackbar({
          open: true,
          message:
            'Aucune maison de repos en base. Un administrateur doit en ajouter via le menu « Maisons de repos ».',
          severity: 'warning',
        });
      }
    } catch (error) {
      if (isStale()) return;
      console.error('Erreur lors du chargement des données:', error);
      const apiMsg = error?.response?.data?.error;
      const url = error?.config?.url ? String(error.config.url) : '';
      const isMaisonsPath = url.includes('maisons-repos');
      if (error?.response?.status === 401) {
        setSnackbar({
          open: true,
          message:
            'Session expirée ou non reconnue — reconnectez-vous avec la même adresse (URL) que d’habitude.',
          severity: 'error',
        });
      } else if (isMaisonsPath && apiMsg) {
        setSnackbar({ open: true, message: `Maisons de repos : ${apiMsg}`, severity: 'error' });
      } else {
        setSnackbar({
          open: true,
          message: apiMsg
            ? apiMsg
            : 'Erreur lors du chargement des données (clients, articles ou maisons).',
          severity: 'error',
        });
      }
    } finally {
      if (!isStale()) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const cancelRef = { cancelled: false };
    fetchData(cancelRef);
    return () => {
      cancelRef.cancelled = true;
    };
  }, [fetchData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFactureData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    // Si "Autre" est sélectionné, réinitialiser le champ personnalisé
    if (name === 'maison_repos' && value !== 'Autre') {
      setCustomMaisonRepos('');
    }
    
    // Si un nom personnalisé est sélectionné depuis le menu déroulant, le mettre dans le champ personnalisé
    if (name === 'maison_repos' && value !== 'Autre' && !nomsMaisonsApi.has(value)) {
      setCustomMaisonRepos(value);
    }
  };

  const handleCustomMaisonReposChange = (e) => {
    const value = e.target.value;
    setCustomMaisonRepos(value);
    // Ne pas mettre à jour factureData immédiatement pour éviter les conflits
  };

  const handleCustomMaisonReposBlur = () => {
    // Mettre à jour factureData seulement quand on quitte le champ
    if (customMaisonRepos && customMaisonRepos.trim() !== '') {
      const trimmedValue = customMaisonRepos.trim();
      setFactureData(prev => ({
        ...prev,
        maison_repos: trimmedValue,
      }));

      // Ajouter le nom personnalisé à la liste s'il n'existe pas déjà
      setMaisonsRepos(prev => {
        const exists = prev.some(maison => maison.value === trimmedValue);
        if (!exists) {
          // Créer une nouvelle liste avec le nom personnalisé ajouté avant "Autre"
          const newList = prev.filter(maison => maison.value !== 'Autre');
          return [
            ...newList,
            { value: trimmedValue, label: trimmedValue },
            { value: 'Autre', label: 'Autre' }
          ];
        }
        return prev;
      });
    }
  };

  const handleEditMaisonRepos = (value) => {
    setEditingMaisonRepos(value);
    setEditingValue(value);
  };

  const handleSaveEdit = () => {
    if (editingValue && editingValue.trim() !== '') {
      const trimmedValue = editingValue.trim();
      
      // Mettre à jour la liste
      setMaisonsRepos(prev => {
        return prev.map(maison => 
          maison.value === editingMaisonRepos 
            ? { value: trimmedValue, label: trimmedValue }
            : maison
        );
      });

      // Mettre à jour factureData si c'est la valeur actuellement sélectionnée
      if (factureData.maison_repos === editingMaisonRepos) {
        setFactureData(prev => ({
          ...prev,
          maison_repos: trimmedValue,
        }));
      }

      setEditingMaisonRepos(null);
      setEditingValue('');
    }
  };

  const handleCancelEdit = () => {
    setEditingMaisonRepos(null);
    setEditingValue('');
  };

  const handleDeleteMaisonRepos = (value) => {
    if (value === 'Autre' || nomsMaisonsApi.has(value)) return;
    setMaisonsRepos((prev) => prev.filter((maison) => maison.value !== value));

    if (factureData.maison_repos === value) {
      const first = nomsMaisonsApi.size ? [...nomsMaisonsApi][0] : '';
      setFactureData((prev) => ({
        ...prev,
        maison_repos: first,
      }));
      setCustomMaisonRepos('');
    }
  };

  const handleDateChange = (field, value) => {
    setFactureData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleClientChange = (event, newValue) => {
    setSelectedClient(newValue);
    setFactureData(prev => ({
      ...prev,
      client_id: newValue ? newValue.id : '',
    }));
  };

  const handleLigneChange = (e) => {
    const { name, value } = e.target;
    setNewLigne(prev => ({
      ...prev,
      [name]: value,
    }));

    // Si l'article change, mettre à jour le prix et la TVA
    if (name === 'article_id') {
      const selectedArticle = articles.find(article => article.id === parseInt(value));
      if (selectedArticle) {
        setNewLigne(prev => ({
          ...prev,
          prix_unitaire: selectedArticle.prix_ht,
          tva: selectedArticle.tva,
        }));
      }
    }
  };

  const addLigne = () => {
    if (!newLigne.article_id || newLigne.quantite <= 0) {
      showSnackbar('Veuillez sélectionner un article et une quantité', 'warning');
      return;
    }

    const selectedArticle = articles.find(article => article.id === parseInt(newLigne.article_id));
    if (!selectedArticle) return;

    const ligne = {
      ...newLigne,
      article_id: parseInt(newLigne.article_id),
      quantite: parseFloat(newLigne.quantite),
      prix_unitaire: parseFloat(newLigne.prix_unitaire),
      tva: parseFloat(newLigne.tva),
      article_nom: selectedArticle.nom,
      article_reference: selectedArticle.reference,
    };

    setFactureData(prev => ({
      ...prev,
      lignes: [...prev.lignes, ligne],
    }));

    // Réinitialiser le formulaire de ligne
    setNewLigne({
      article_id: '',
      quantite: 1,
      prix_unitaire: 0,
      tva: 21.0,
    });
  };

  const removeLigne = (index) => {
    setFactureData(prev => ({
      ...prev,
      lignes: prev.lignes.filter((_, i) => i !== index),
    }));
  };

  const calculateTotals = () => {
    const totals = factureData.lignes.reduce(
      (acc, ligne) => {
        const ligne_ht = ligne.quantite * ligne.prix_unitaire;
        const ligne_tva = ligne_ht * (ligne.tva / 100);
        const ligne_ttc = ligne_ht + ligne_tva;

        return {
          total_ht: acc.total_ht + ligne_ht,
          total_tva: acc.total_tva + ligne_tva,
          total_ttc: acc.total_ttc + ligne_ttc,
        };
      },
      { total_ht: 0, total_tva: 0, total_ttc: 0 }
    );

    return totals;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!factureData.client_id) {
      showSnackbar('Veuillez sélectionner un client', 'warning');
      return;
    }

    if (!factureData.maison_repos) {
      showSnackbar('Veuillez sélectionner une maison de repos', 'warning');
      return;
    }

    if (factureData.lignes.length === 0) {
      showSnackbar('Veuillez ajouter au moins une ligne', 'warning');
      return;
    }

    try {
      const factureToSend = {
        ...factureData,
        client_id: parseInt(factureData.client_id),
        date_facture: format(factureData.date_facture, 'yyyy-MM-dd'),
        date_echeance: factureData.date_echeance 
          ? format(factureData.date_echeance, 'yyyy-MM-dd')
          : null,
      };

      await axios.post('/api/factures', factureToSend);
      showSnackbar('Facture créée avec succès', 'success');
      navigate('/factures');
    } catch (error) {
      console.error('Erreur lors de la création de la facture:', error);
      showSnackbar('Erreur lors de la création de la facture', 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleOpenClientDialog = () => {
    setNewClientData({
      nom: '',
      prenom: '',
      email: '',
      telephone: '',
      adresse: '',
      code_postal: '',
      ville: '',
      pays: 'Belgique',
      numero_tva: '',
      maison_repos: '',
    });
    setOpenClientDialog(true);
  };

  const handleCloseClientDialog = () => {
    setOpenClientDialog(false);
  };

  const handleNewClientInputChange = (e) => {
    const { name, value } = e.target;
    setNewClientData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCreateClient = async () => {
    try {
      const response = await axios.post('/api/clients', newClientData);
      const newClient = response.data;
      
      // Ajouter le nouveau client à la liste
      setClients(prev => [...prev, newClient]);
      
      // Sélectionner automatiquement le nouveau client
      setSelectedClient(newClient);
      setFactureData(prev => ({
        ...prev,
        client_id: newClient.id,
      }));
      
      setOpenClientDialog(false);
      showSnackbar('Client créé avec succès', 'success');
    } catch (error) {
      console.error('Erreur lors de la création du client:', error);
      showSnackbar('Erreur lors de la création du client', 'error');
    }
  };

  const totals = calculateTotals();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Chargement...</Typography>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', pb: 8 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h4">
            Nouvelle facture
          </Typography>
        </Box>

        <form onSubmit={handleSubmit} style={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Grid container spacing={2} sx={{ flexGrow: 1 }}>
            {/* Informations générales */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Informations générales
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" gap={1}>
                      <Autocomplete
                        options={clients}
                        getOptionLabel={(option) => 
                          option.prenom ? `${option.prenom} ${option.nom}` : option.nom
                        }
                        value={selectedClient}
                        onChange={handleClientChange}
                        loading={loading}
                        sx={{ flexGrow: 1 }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Rechercher un client"
                            required
                            placeholder="Tapez le nom du client..."
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <>
                                  <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                  {params.InputProps.startAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <Box component="li" {...props}>
                            <Box>
                              <Typography variant="body1">
                                {option.prenom ? `${option.prenom} ${option.nom}` : option.nom}
                              </Typography>
                              {option.email && (
                                <Typography variant="body2" color="text.secondary">
                                  {option.email}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        )}
                        noOptionsText="Aucun client trouvé"
                        loadingText="Chargement..."
                      />
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={handleOpenClientDialog}
                        sx={{ minWidth: 'auto', px: 2 }}
                      >
                        Nouveau
                      </Button>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      label="Date de facture"
                      value={factureData.date_facture}
                      onChange={(value) => handleDateChange('date_facture', value)}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      label="Date d'échéance (optionnel)"
                      value={factureData.date_echeance}
                      onChange={(value) => handleDateChange('date_echeance', value)}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </Grid>
                  {/* Champ statut supprimé - la colonne n'existe plus */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="notes"
                      label="Notes (optionnel)"
                      value={factureData.notes}
                      onChange={handleInputChange}
                      fullWidth
                      multiline
                      rows={3}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Maison de repos *</InputLabel>
                      <Select
                        name="maison_repos"
                        value={factureData.maison_repos}
                        onChange={handleInputChange}
                        label="Maison de repos *"
                        required
                      >
                        {maisonsRepos.map((maison, idx) => (
                          <MenuItem
                            key={maison.id != null ? `mr-${maison.id}` : `mr-${idx}-${maison.value}`}
                            value={maison.value}
                          >
                            <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                              <span>{maison.label}</span>
                              {!maison.fromApi && maison.value !== 'Autre' && (
                                <Box display="flex" gap={0.5} onClick={(e) => e.stopPropagation()}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditMaisonRepos(maison.value)}
                                    sx={{ p: 0.5 }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteMaisonRepos(maison.value)}
                                    sx={{ p: 0.5 }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              )}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {factureData.maison_repos === 'Autre' && (
                      <TextField
                        label="Nom personnalisé"
                        value={customMaisonRepos}
                        onChange={handleCustomMaisonReposChange}
                        onBlur={handleCustomMaisonReposBlur}
                        fullWidth
                        placeholder="Saisir le nom"
                        sx={{ mt: 1 }}
                      />
                    )}
                    {editingMaisonRepos && (
                      <Box display="flex" alignItems="center" gap={1} sx={{ mt: 1 }}>
                        <TextField
                          label="Modifier le nom"
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          size="small"
                          sx={{ flexGrow: 1 }}
                        />
                        <IconButton size="small" onClick={handleSaveEdit} color="primary">
                          <SaveIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={handleCancelEdit} color="secondary">
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Ajout de lignes */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Lignes de facture
                </Typography>
                
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth>
                      <InputLabel>Article</InputLabel>
                      <Select
                        name="article_id"
                        value={newLigne.article_id}
                        onChange={handleLigneChange}
                        label="Article"
                      >
                        {articles.map((article) => (
                          <MenuItem key={article.id} value={article.id}>
                            {article.reference} - {article.nom}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      name="quantite"
                      label="Quantité"
                      type="number"
                      value={newLigne.quantite}
                      onChange={handleLigneChange}
                      fullWidth
                      inputProps={{ min: 0.01, step: 0.01 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      name="prix_unitaire"
                      label="Prix unitaire"
                      type="number"
                      value={newLigne.prix_unitaire}
                      onChange={handleLigneChange}
                      fullWidth
                      inputProps={{ min: 0, step: 0.01 }}
                      InputProps={{
                        endAdornment: <Typography variant="body2">€</Typography>,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      name="tva"
                      label="TVA (%)"
                      type="number"
                      value={newLigne.tva}
                      onChange={handleLigneChange}
                      fullWidth
                      inputProps={{ min: 0, max: 100, step: 0.1 }}
                      InputProps={{
                        endAdornment: <Typography variant="body2">%</Typography>,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={addLigne}
                      sx={{ height: '56px' }}
                      fullWidth
                    >
                      Ajouter
                    </Button>
                  </Grid>
                </Grid>

                {/* Tableau des lignes */}
                {factureData.lignes.length > 0 && (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Article</TableCell>
                          <TableCell>Quantité</TableCell>
                          <TableCell>Total TTC</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {factureData.lignes.map((ligne, index) => {
                          const ligne_ht = ligne.quantite * ligne.prix_unitaire;
                          const ligne_tva = ligne_ht * (ligne.tva / 100);
                          const ligne_ttc = ligne_ht + ligne_tva;

                          return (
                            <TableRow key={index}>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                  {ligne.article_nom}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  {ligne.article_reference}
                                </Typography>
                              </TableCell>
                              <TableCell>{ligne.quantite}</TableCell>
                              <TableCell>{ligne_ttc.toFixed(2)} €</TableCell>
                              <TableCell align="center">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => removeLigne(index)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Grid>

            {/* Totaux */}
            {factureData.lignes.length > 0 && (
              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  <Grid container justifyContent="flex-end">
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="h6" gutterBottom>
                          Récapitulatif
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                            Total TTC: {totals.total_ttc.toFixed(2)} €
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            )}

            {/* Boutons d'action */}
            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between">
                <Button
                  variant="outlined"
                  onClick={() => navigate('/factures')}
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={factureData.lignes.length === 0}
                >
                  Créer la facture
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Dialogue pour ajouter un nouveau client */}
        <Dialog open={openClientDialog} onClose={handleCloseClientDialog} maxWidth="md" fullWidth>
          <DialogTitle>Nouveau client</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="nom"
                  label="Nom *"
                  value={newClientData.nom}
                  onChange={handleNewClientInputChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="prenom"
                  label="Prénom"
                  value={newClientData.prenom}
                  onChange={handleNewClientInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="email"
                  label="Email"
                  type="email"
                  value={newClientData.email}
                  onChange={handleNewClientInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="telephone"
                  label="Téléphone"
                  value={newClientData.telephone}
                  onChange={handleNewClientInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="adresse"
                  label="Adresse"
                  value={newClientData.adresse}
                  onChange={handleNewClientInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  name="code_postal"
                  label="Code postal"
                  value={newClientData.code_postal}
                  onChange={handleNewClientInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  name="ville"
                  label="Ville"
                  value={newClientData.ville}
                  onChange={handleNewClientInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  name="pays"
                  label="Pays"
                  value={newClientData.pays}
                  onChange={handleNewClientInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="numero_tva"
                  label="Numéro TVA"
                  value={newClientData.numero_tva}
                  onChange={handleNewClientInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="maison_repos"
                  label="Maison de repos"
                  value={newClientData.maison_repos}
                  onChange={handleNewClientInputChange}
                  fullWidth
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseClientDialog}>Annuler</Button>
            <Button 
              onClick={handleCreateClient} 
              variant="contained"
              disabled={!newClientData.nom.trim()}
            >
              Créer le client
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Footer avec mention Woud */}
        <Footer />
      </Box>
    </LocalizationProvider>
  );
}

export default NouvelleFacture; 